<template>
    <!-- Confirm Password -->
    <div class="row">
        <div class="col-sm-12 mb-3" v-if="header">
            <div class="d-flex flex-column gap-3 text-center align-items-center">
	            <i class="bi w-auto h-auto fs-large bi-shield-lock"></i>
                <h3 class="fw-semi-bold m-0">Change Password</h3>
                <p class="text-secondary m-0">Please enter your new password in the fields given below.</p>
            </div>
            <div v-if="formErrors.password" class="validation-error mt-0 text-center" v-html="formErrors.password"></div>
        </div>

        <div ref="changePasswordForm" class="col-12">

<!--	        <label class="form-label" for="password">New Password</label>-->
	        <div class="d-flex align-items-center justify-content-between rounded-pill border py-2 px-3 input-container border-active-darken mb-3" :class="formErrors.password ? 'border-danger' : 'border-secondary'">
		        <label for="passwordField">
			        <i class="me-3 dd-icon bi bi-shield-lock"></i>
		        </label>
		        <input id="passwordField" ref="passwordField" v-model="password" :type="passwordToggle ? 'text' : 'password'" class="form-control m-0 border-0 fs-5 p-0 lh-fixed" name="password" placeholder="Enter New Password" autofocus @input="removePassErrorKeys">
		        <span class="rounded-circle">
                    <i :class="passwordToggle ? 'bi-eye' : 'bi-eye-slash'" @click="(event) => passwordToggle = !passwordToggle"></i>
                </span>
	        </div>

<!--	        <label class="form-label" for="password_confirmation">Password Confirmation</label>-->
	        <div class="d-flex align-items-center justify-content-between rounded-pill border py-2 px-3 input-container border-active-darken mb-3" :class="formErrors.password ? 'border-danger' : 'border-secondary'">
		        <label for="password_confirmation">
			        <i class="me-3 dd-icon bi bi-shield-check"></i>
		        </label>
		        <input id="password_confirmation" v-model="password_confirmation" :type="confirmPasswordToggle ? 'text' : 'password'" class="form-control m-0 border-0 fs-5 p-0 lh-fixed" name="password_confirmation" placeholder="Confirm New Password" @input="removePassErrorKeys" @keyup.enter="changePassword">
		        <span class="rounded-circle">
                    <i :class="confirmPasswordToggle ? 'bi-eye' : 'bi-eye-slash'" @click="(event) => confirmPasswordToggle = !confirmPasswordToggle"></i>
                </span>
	        </div>
	        
	        <div class="d-flex flex-row align-items-center justify-content-center gap-3 text-center ">
                <button class="btn btn-outline-secondary rounded-pill py-2 px-3" @click="close">Cancel</button>
                <button v-if="!autoSubmit" class="btn btn-dark rounded-pill py-2 px-3" @click="changePassword" :disabled="hasFormErrors">Save Changes</button>
            </div>
        </div>
    </div>

</template>

<script setup>
import { computed, watch, ref, onMounted, nextTick } from "vue";
import apiClient from "@/utils/axios.js";
import {useAppHelpers} from "@/composables/useAppHelpers.js";
import {useHttpErrorManager} from "@/composables/useHttpErrorManager.js";
import { useAuthStore } from "@/stores/auth-store.js";
import { useToast } from "@/composables/useToast.js";

const props = defineProps({
	header: { type: Boolean, required: false, default: true },
	autoSubmit: { type: Boolean, required: false, default: false }
});

const emits = defineEmits(['closeChangePassword']);

const auth = useAuthStore();
const helper = useAppHelpers();
const { displayToast } = useToast();

const changePasswordForm = ref(null);

const user = ref(auth.user);
watch(() => auth.user, (newVal) => {
	user.value = newVal;
});

const formErrors = ref({});

const password = ref("");
const password_confirmation = ref("");

const passwordToggle = ref(false);
const confirmPasswordToggle = ref(false);

const passwordField = ref(null);
onMounted(() => {
	nextTick(() => {
		passwordField.value.focus();
	});
});

const removePassErrorKeys = () => {
	removeFormErrorKey("password");
	removeFormErrorKey("password_confirmation");
};

const removeFormErrorKey = (key) => {
	delete formErrors.value[key];
};

const hasFormErrors = computed(() => {
	return Object.keys(formErrors.value).length > 0;
});

const validatePassword = () => {
	if (!password.value) {
		// helper.toast('Please enter your password to continue.', 'error');
		formErrors.value.password = "Please enter your password to continue.";
		return false;
	}
	if (password.value.length < 8) {
		// helper.toast('Password must be at least 8 characters long.', 'error');
		formErrors.value.password = "Password must be at least 8 characters long.";
		return false;
	}

	const number = /[0-9]/g;
	const upperCase = /[A-Z]/g;
	const specialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;

	if (!password.value.match(upperCase)) {
		// helper.toast('Password must contain at least one uppercase letter.', 'error');
		formErrors.value.password = "Password must contain at least one uppercase letter.";
		return false;
	}
	if (!password.value.match(number)) {
		// helper.toast('Password must contain at least one number.', 'error');
		formErrors.value.password = "Password must contain at least one number.";
		return false;
	}
	if (!password.value.match(specialChar)) {
		// helper.toast('Password must contain at least one special character.', 'error');
		formErrors.value.password = "Password must contain at least one special character.";
		return false;
	}
	if (!password_confirmation.value) {
		// helper.toast('Please confirm your password to continue.', 'error');
		formErrors.value.password_confirmation = "Please confirm your password.";
		return false;
	}
	if (password.value !== password_confirmation.value) {
		// helper.toast('Passwords do not match.', 'error');
		formErrors.value.password = "Password and it's confirmation do not match.";
		return false;
	}

	return true;
};

const changePassword = async () => {
	helper.loaderOn(changePasswordForm.value);


	if (!validatePassword()) {
		helper.loaderOff(changePasswordForm.value);
		return;
	}

	try {
		const response = await apiClient.post(`auth/user/changePassword`, {
			password: password.value,
			password_confirmation: password_confirmation.value
		});
		if (response.status === 200) {
			password.value = "";
			password_confirmation.value = "";
			displayToast('Password has been changed successfully.', 'outline-success', 8000);
			await close();
		}
		helper.loaderOff(changePasswordForm.value);
	}
	catch (error) {
		let errorObj = await useHttpErrorManager().handleError(error, false);
		if (error.response.status === 422) {
			formErrors.value = errorObj;
			removeFormErrorKey("full_message");
			removeFormErrorKey("alt_message");
			removeFormErrorKey("err_message");
		}
		helper.loaderOff(changePasswordForm.value);
	}
};

const close = async () => {
	emits('closeChangePassword');
}

defineExpose({
    close
});
</script>
